import React, { useState } from "react";
import { motion } from "framer-motion";
import LucideIcon from "utils/LucideIcon";
import { IPrivacyAndTermsPage } from "Interfaces/interface.informationalPages";

const PrivacyPolicyPage: React.FC<IPrivacyAndTermsPage> = ({
  websiteDomain = "gifthub.360customizer.com",
  companyName = "360Customizer",
  companyAddress = "2nd Floor A wing, Pathak Park, 360Customizer (Namrup Technologies Pvt Ltd), next to Tech Mahindra, behind Anand veg restaurant, Bhalekar Chawl, Erandwane, Pune, Maharashtra 411004",
  officerName = "Vikas Tiwari",
  officerEmail = "vikas.tiwari@360customizer.com",
  companyEmail = "sales@360customizer.com",
  companyContact = "+91-8637758689",
}) => {
  const [activeSection, setActiveSection] = useState<string>("introduction");

  const privacySectionData = [
    {
      id: "introduction",
      title: "Introduction",
      content: [
        `This document is an electronic record under the Information Technology Act, 2000, and relevant rules, including amendments related to electronic records. It is generated by a computer system and does not require physical or digital signatures.`,
        `The domain name ${websiteDomain} (hereinafter referred to as the "Website") is owned by ${companyName}, with its registered office at ${companyAddress}.`,
        `${companyName} operates Gifthub, a comprehensive corporate gifting platform that enables businesses to discover, customize, and inquire about personalized products. This Privacy Policy explains how we collect, use, and protect your information when you use our services.`,
        `You can browse the Website without revealing personal information. However, certain features such as account creation, product customization, AI assistance, and inquiry submission require you to provide personal data, which is handled as per this Privacy Policy.`,
      ],
    },
    {
      id: "data-collection",
      title: "Information We Collect",
      content: [
        `Personal Information: We collect your name, email address, phone number, company name, and business address when you create an account or submit inquiries.`,
        `Authentication Data: We store encrypted passwords and authentication tokens for secure login. We also support social login through Google OAuth, which provides us with your basic profile information.`,
        `User-Generated Content: We collect company logos, custom designs, product customizations, and any files you upload to our platform for personalization purposes.`,
        `Usage Data: We track your product browsing history, search queries, inquiry submissions, and interactions with our AI assistant to improve our services.`,
        `Technical Data: We collect your IP address, device fingerprint, browser type, operating system, and other technical information for security and analytics purposes.`,
        `Communication Data: We store chat messages with our AI assistant, support communications, and any feedback you provide to help improve our services.`,
      ],
    },
    {
      id: "data-usage",
      title: "How We Use Your Information",
      content: [
        `Service Provision: We use your information to create and manage your account, process inquiries, generate product mockups, and provide AI-powered recommendations.`,
        `Customization Services: Your uploaded logos and preferences are used to create personalized product mockups and catalogs according to your specifications.`,
        `Communication: We use your contact information to send inquiry confirmations, account notifications, and respond to your inquiries.`,
        `AI Processing: Your interactions with our AI assistant and product preferences are analyzed to provide personalized recommendations and improve our AI algorithms.`,
        `Platform Improvement: We analyze usage patterns and feedback to enhance our platform features, user experience, and service quality.`,
        `Legal Compliance: We may use your information to comply with applicable laws, regulations, and legal processes.`,
      ],
    },
    {
      id: "third-party-services",
      title: "Third-Party Services",
      content: [
        `Authentication Services: We use secure third-party authentication services to manage user accounts and ensure platform security.`,
        `Analytics Services: We use analytics services to understand website usage patterns and improve user experience. These services may collect cookies and usage data.`,
        `Social Login: We integrate social login services for convenient account creation and login.`,
        `Cloud Storage: Your uploaded files and generated content are stored securely on cloud infrastructure with appropriate access controls.`,
        `Real-time Communication: We use real-time communication services to provide live updates on inquiry status, cart changes, and catalog generation progress.`,
        `Payment Processing: When you make purchases, your payment information is processed through secure, PCI-compliant payment gateways. We do not store your complete payment card details.`,
      ],
    },
    {
      id: "cookies-tracking",
      title: "Cookies and Tracking",
      content: [
        `Essential Cookies: We use cookies to maintain your login session, remember your cart contents, and ensure platform functionality. These cookies are necessary for the platform to work properly.`,
        `Functional Cookies: We store your preferences, customization settings, and user interface choices to provide a personalized experience across sessions.`,
        `Analytics Cookies: Analytics cookies help us understand how users interact with our platform, which pages are most popular, and how we can improve our services.`,
        `Security Measures: We use various security techniques for fraud prevention and to maintain session integrity.`,
        `Cookie Management: You can control cookie preferences through your browser settings. However, disabling essential cookies may affect platform functionality.`,
      ],
    },
    {
      id: "data-retention",
      title: "Data Retention",
      content: [
        `Account Information: We retain your account data until you request account deletion or the account becomes inactive for more than 2 years.`,
        `Order Data: We keep inquiry and transaction records for 7 years to comply with tax and legal requirements.`,
        `User-Generated Content: Your uploaded logos and customizations are retained until you delete them or close your account.`,
        `AI Chat Logs: We store chat conversations with our AI assistant for 2 years to improve our AI services and provide better recommendations.`,
        `Analytics Data: Analytics data is retained according to standard industry practices.`,
        `Backup Data: We maintain encrypted backups of your data for disaster recovery purposes, which are automatically purged according to our retention schedule.`,
      ],
    },
    {
      id: "user-rights",
      title: "Your Rights and Choices",
      content: [
        `Access Rights: You can request access to all personal information we have collected about you by contacting us at ${companyEmail}.`,
        `Data Portability: You can request a copy of your data in a machine-readable format for transfer to another service.`,
        `Correction Rights: You can update your account information, contact details, and preferences through your account settings or by contacting our support team.`,
        `Deletion Rights: You can request deletion of your account and associated data, subject to legal and operational requirements.`,
        `Marketing Opt-out: You can unsubscribe from marketing communications by clicking the unsubscribe link in emails or contacting us directly.`,
        `Cookie Preferences: You can manage cookie settings through your browser preferences or our cookie consent banner.`,
        `AI Data Processing: You can opt out of AI-powered recommendations and data processing for machine learning purposes by contacting our support team.`,
      ],
    },
    {
      id: "data-security",
      title: "Data Security",
      content: [
        `Encryption: All sensitive data is encrypted in transit using HTTPS/TLS protocols and at rest using AES-256 encryption standards.`,
        `Access Controls: We implement role-based access controls and multi-factor authentication to ensure only authorized personnel can access your data.`,
        `Secure Infrastructure: Our platform is hosted on secure cloud infrastructure with regular security updates and monitoring.`,
        `Regular Audits: We conduct regular security assessments, vulnerability scans, and penetration testing to identify and address potential security issues.`,
        `Incident Response: We have established procedures for detecting, reporting, and responding to data security incidents, including breach notification requirements.`,
        `Employee Training: Our team receives regular training on data protection best practices and security protocols.`,
      ],
    },
    {
      id: "data-sharing",
      title: "Information Sharing",
      content: [
        `Service Providers: We share information with trusted third-party service providers who assist in operating our platform. These providers are bound by confidentiality agreements.`,
        `Legal Requirements: We may disclose your information when required by law, court order, or government request, or to protect our rights and the safety of our users.`,
        `Business Transfers: In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction.`,
        `User Consent: We may share your information with third parties when you explicitly consent to such sharing, such as for specific promotional offers or partnerships.`,
        `Aggregated Data: We may share anonymized, aggregated data that cannot identify you individually for research, analytics, or business purposes.`,
        `No Sale of Data: We do not sell, rent, or trade your personal information to third parties for their marketing purposes.`,
      ],
    },
    {
      id: "international-transfers",
      title: "International Data Transfers",
      content: [
        `Global Operations: As part of our global operations, your data may be transferred to and processed in countries other than your country of residence.`,
        `Adequate Protection: We ensure that any international transfers are conducted with appropriate safeguards, including standard contractual clauses and adequacy decisions.`,
        `Cloud Services: Our use of cloud services may involve data processing in various global locations, all of which maintain high security standards.`,
        `Compliance: We comply with applicable data protection laws regarding international transfers, including GDPR requirements for EU residents.`,
      ],
    },
    {
      id: "children-privacy",
      title: "Children's Privacy",
      content: [
        `Age Restrictions: Our platform is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18.`,
        `Parental Consent: If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information promptly.`,
        `Business Focus: Our platform is designed for corporate and business use, which inherently requires users to be adults with business authorization.`,
      ],
    },
    {
      id: "policy-updates",
      title: "Policy Updates",
      content: [
        `Regular Review: We review and update this Privacy Policy regularly to reflect changes in our practices, technology, and legal requirements.`,
        `Notification of Changes: We will notify you of material changes to this policy through email notifications or prominent notices on our platform.`,
        `Continued Use: Your continued use of our platform after policy updates constitutes acceptance of the revised terms.`,
        `Version Control: We maintain version history of our Privacy Policy, and previous versions are available upon request.`,
      ],
    },
    {
      id: "grievance-officer",
      title: "Grievance Officer",
      content: [
        `In accordance with the Information Technology Act, 2000, any grievances regarding the handling of your Personal Information should be directed to our Grievance Officer.`,
        `We are committed to addressing your privacy concerns promptly and transparently. You can contact our Grievance Officer for any questions, complaints, or requests related to your personal data.`,
      ],
      includeContact: true,
    },
  ];

  const sectionData = privacySectionData.find((s: any) => s.id === activeSection);

  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "data-collection", title: "Information We Collect" },
    { id: "data-usage", title: "How We Use Your Information" },
    { id: "third-party-services", title: "Third-Party Services" },
    { id: "cookies-tracking", title: "Cookies and Tracking" },
    { id: "data-retention", title: "Data Retention" },
    { id: "user-rights", title: "Your Rights and Choices" },
    { id: "data-security", title: "Data Security" },
    { id: "data-sharing", title: "Information Sharing" },
    { id: "international-transfers", title: "International Data Transfers" },
    { id: "children-privacy", title: "Children's Privacy" },
    { id: "policy-updates", title: "Policy Updates" },
    { id: "grievance-officer", title: "Grievance Officer" },
  ];

  const isActive = (sectionId: string) => activeSection === sectionId;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <motion.div
      className="min-h-screen"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <motion.div className="overflow-hidden md:p-8" variants={itemVariants}>
          {/* Header */}
          <motion.h1
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="flex items-center md:text-left justify-center md:justify-start mb-4">
              <LucideIcon
                name="Shield"
                className="w-10 h-10 text-blue-900 hover:text-blue-900 hidden md:inline-block mr-3"
              />
              <h1 className="text-4xl md:text-5xl font-bold font-inter text-secondary-gradient bg-clip-text text-transparent pb-1">
                Privacy Policy
              </h1>
            </div>
          </motion.h1>
          <motion.div
            className="w-24 h-1 bg-secondary-gradient mx-auto md:mx-0 md:ml-14 mb-8 rounded-xl"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ amount: 0.2 }}
          ></motion.div>

          {/* Content */}
          <motion.div
            className="prose prose-lg max-w-none"
            variants={itemVariants}
          >
            {/* Introduction Text */}
            <motion.p
              className="text-gray-700 leading-relaxed mb-8 text-base font-inter"
              variants={itemVariants}
            >
              Welcome to{" "}
              <span className="font-semibold text-indigo-400">
                Gifthub
              </span>
              . This Privacy Policy outlines how we collect, use, and protect
              your personal information when you use our Website, located at{" "}
              <span className="font-semibold text-indigo-400">
                {websiteDomain}
              </span>
              . By accessing this Website, you agree to the terms of this
              Privacy Policy. Do not continue to use {websiteDomain} if you do
              not agree with the terms outlined in this policy.
            </motion.p>

            <motion.div
              className="bg-gradient-to-r from-blue-100 to-indigo-100 border-l-4 border-red-500 p-6 rounded-r-lg mb-8"
              variants={itemVariants}
            >
              <h4 className="font-bold text-red-500 text-xl font-inter mb-2">
                IMPORTANT NOTICE
              </h4>
              <p className="text-red-400 font-inter">
                BY USING THE WEBSITE, YOU AGREE TO THE TERMS AND CONDITIONS OF
                THIS POLICY. IF YOU DO NOT AGREE WITH THE TERMS AND CONDITIONS
                OF THIS POLICY, PLEASE DO NOT PROCEED FURTHER TO USE THIS
                WEBSITE.
              </p>
            </motion.div>

            {/* Navigation */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-8"
              variants={itemVariants}
            >
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${isActive(section.id)
                      ? "bg-indigo-600 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {section.title}
                </button>
              ))}
            </motion.div>

            {/* Content Sections */}
            {sectionData && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-indigo-50 rounded-xl p-6"
              >
                <h4 className="text-2xl font-bold font-inter text-secondary-gradient bg-clip-text text-transparent pb-1 mb-4">
                  {sectionData.title}
                </h4>
                <div className="space-y-4 text-gray-700 font-inter text-base">
                  {sectionData.content.map((para, index) => (
                    <p key={index}>{para}</p>
                  ))}
                </div>

                {sectionData.includeContact && (
                  <div className="bg-white rounded-lg p-4 border border-indigo-200 mt-6">
                    <h5 className="font-bold text-xl text-gray-800 mb-3">
                      Contact Information
                    </h5>
                    <div className="text-gray-700">
                      <p className="font-inter text-base">
                        <strong>Officer Incharge:</strong> {officerName} (
                        {companyName})
                      </p>
                      <p className="font-inter text-base">
                        <strong>Email:</strong> {officerEmail}
                      </p>
                      <p className="font-inter text-base">
                        <strong>Phone:</strong> {companyContact}
                      </p>
                      <p className="font-inter text-base">
                        <strong>Address:</strong> {companyAddress}
                      </p>
                      <p className="font-inter text-base">
                        <strong>Timings:</strong> 10 AM to 6 PM, Monday to
                        Saturday, except public holidays
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default PrivacyPolicyPage;
